import {makeCompletionPattern} from "./factory";
import {FieldAccessTree, IdentTree} from "../model";
import {Misc, Separator} from "../lexer";
import {
	listFields,
	listMethods,
	ScopeChainNode,
	StaticPolicy,
} from "../analysis";
import {Completion, CompletionResult} from "@codemirror/autocomplete";
import {completionForInfo} from "./names";

function completeFieldAccess(
	scope: ScopeChainNode,
	access: FieldAccessTree
): CompletionResult | null {
	const lhs = access.lhs;
	if (!lhs.javaType) return null;

	const filter = access.rhs?.prettyPrint() ?? "";

	const ty = lhs.javaType;
	if (ty.type === "any" || ty.type === "null" || ty.type === "primitive")
		return null;

	const methods = listMethods(scope, StaticPolicy.NONSTATIC_ONLY, ty).filter(
		(m) => !m.val.isCtor
	);
	const fields = listFields(scope, StaticPolicy.NONSTATIC_ONLY, ty);

	const methodsAndFields = [...methods, ...fields];

	const completions: Completion[] = [];
	for (const thunk of methodsAndFields) {
		if (
			thunk.val.name.startsWith(filter) &&
			(thunk.val.type !== "function" || !thunk.val.isCtor)
		) {
			const thunked = thunk.get();
			completions.push(completionForInfo(thunked));
		}
	}

	if (completions.length > 0) {
		return {
			from: access.dotToken.span.end,
			options: completions,
		};
	}

	return null;
}

// Completion: this.|
const fieldAccess1 = makeCompletionPattern(
	["any", FieldAccessTree, Separator.Dot] as const,
	(chain, scope, parent, access) => {
		return completeFieldAccess(scope, access);
	}
);

// Completion: this.a|
const fieldAccess2 = makeCompletionPattern(
	["any", FieldAccessTree, IdentTree, Misc.identifier] as const,
	(chain, scope, parent, access) => {
		return completeFieldAccess(scope, access);
	}
);

export const FIELD_ACCESS = [fieldAccess1, fieldAccess2];
